import { useCallback, useEffect, useState } from "react";
import { useViimeisin } from "./tuoreus";
import { naytaPvm } from "./pvm";
import { api, ApiError, type Laippaliitos } from "./api";
import { t, esim, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Laippaliitokset (133, Toni 18.9.2026).

   Sama lista kelpaa linjalle ja laitteelle, koska liitos voi
   kuulua kumpaan tahansa tai molempiin — siksi komponentti ottaa
   molemmat tunnisteet ja välittää vain sen, joka on annettu.

   Kuittaus ei kysy momenttia pakollisena: «momentit hyvä olla,
   mihin saatavilla» (Toni). Tyhjä kenttä tarkoittaa kiristettyä
   liitosta ilman lukemaa, ja se on eri asia kuin nolla.
   ═══════════════════════════════════════════════════════════ */

export default function Laippaliitokset({
  linjaId, laiteId, saaKirjata,
}: {
  linjaId?: number;
  laiteId?: number;
  saaKirjata: boolean;
}) {
  const [lista, setLista] = useState<Laippaliitos[] | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [momentti, setMomentti] = useState<Record<number, string>>({});
  const [kuittaa, setKuittaa] = useState<number | null>(null);

  const uusi = useViimeisin();   // päätös 99
  const lataa = useCallback(async () => {
    const tuore = uusi();
    try {
      const l = await api.laippaliitokset({ linja_id: linjaId, laite_id: laiteId });
      if (!tuore()) return;
      setLista(l);
      setVirhe(null);
    } catch (e) {
      if (!tuore()) return;
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.laippa.hakuvirhe"));
      setLista([]);
    }
  }, [linjaId, laiteId, uusi]);

  useEffect(() => { void lataa(); }, [lataa]);

  async function kuittaaLiitos(id: number) {
    setKuittaa(id);
    try {
      const nm = (momentti[id] ?? "").replace(",", ".").trim();
      await api.kuittaaLaippaliitos(id, nm ? { toteutunut_nm: Number(nm) } : {});
      setMomentti((m) => ({ ...m, [id]: "" }));
      await lataa();
      setVirhe(null);
    } catch (e) {
      // Syy nimetään sellaisenaan: pätevyys puuttuu on eri asia kuin
      // «yhtäkään lajia ei ole merkitty kuittaavaksi», ja käyttäjä ei voi
      // korjata jälkimmäistä itse.
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.laippa.kuittausvirhe"));
    } finally { setKuittaa(null); }
  }

  if (lista === null) return <p className="dim">{t("muu.laippa.ladataan")}</p>;

  return (
    <div style={{ marginTop: 12, borderTop: "1px solid var(--viiva)", paddingTop: 12 }}>
      <h3 style={{ margin: "0 0 8px" }}>
        {t("muu.laippa.otsikko")}{" "}
        <span className="dim" style={{ fontWeight: 400 }}>
          {lista.length === 0 ? "" : `· ${t("muu.laippa.avoinna", { n: lista.filter((l) => !l.kuitattu).length, m: lista.length })}`}
        </span>
      </h3>

      {virhe && <p className="huom" role="status">{virhe}</p>}

      {lista.length === 0 ? (
        <p className="dim">{linjaId ? t("muu.laippa.ei_linjalla") : t("muu.laippa.ei_laitteella")}</p>
      ) : (
        <table className="taulu">
          <thead>
            <tr>
              <th>{t("iv.tunnus")}</th><th>{t("muu.laippa.koko")}</th><th>{t("muu.laippa.paineluokka")}</th><th>{t("muu.laippa.tiiviste")}</th>
              <th>{t("muu.laippa.vaadittu")}</th><th>{t("muu.laippa.toteutunut")}</th><th>{t("muu.laippa.kuittaus")}</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((l) => (
              <tr key={l.id}>
                <td className="mono">{l.tunnus}</td>
                <td>{l.koko_dn ?? <span className="dim">–</span>}</td>
                <td>{l.paineluokka ?? <span className="dim">–</span>}</td>
                <td>{l.tiiviste ?? <span className="dim">–</span>}</td>
                <td className="mono">{l.vaadittu_nm ?? <span className="dim">{t("muu.laippa.ei_vaatimusta")}</span>}</td>
                <td className="mono">
                  {l.toteutunut_nm ?? (l.kuitattu
                    ? <span className="dim">{t("muu.laippa.ei_lukemaa")}</span>
                    : <span className="dim">–</span>)}
                </td>
                <td>
                  {l.kuitattu ? (
                    <>{naytaPvm(l.paiva)} · {l.kuittaaja ?? <span className="dim">–</span>}</>
                  ) : saaKirjata ? (
                    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                      <label>{t("muu.laippa.momentti_nm")}
                        <input inputMode="decimal" placeholder={esim("85")} style={{ width: 90 }}
                             value={momentti[l.id] ?? ""}
                             onChange={(e) => setMomentti((m) => ({ ...m, [l.id]: e.target.value }))} /></label>
                      <button className="nappi nappi--kevyt" disabled={kuittaa === l.id}
                              onClick={() => void kuittaaLiitos(l.id)}>
                        {kuittaa === l.id ? t("muu.laippa.kuitataan") : t("muu.laippa.kuittaa")}
                      </button>
                    </span>
                  ) : <span className="dim">{t("psel.avoin")}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="dim" style={{ marginTop: 6 }}>
        {t("muu.laippa.selite")}
      </p>
    </div>
  );
}
